import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useReadingLists } from '../context/ReadingListContext';
import { IoAdd, IoTrash, IoClose, IoBook, IoChevronForward } from 'react-icons/io5';
import './ReadingLists.css';

const EMOJIS = ['📚', '❤️', '🔥', '⭐', '🌙', '🗡️', '🚀', '🎭', '🌊', '💎', '🦋', '🌸'];

export default function ReadingLists() {
  const navigate = useNavigate();
  const { myLists, createList, deleteList, removeBookFromList } = useReadingLists();
  const [showCreate, setShowCreate] = useState(false);
  const [expandedList, setExpandedList] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', emoji: '📚' });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    await createList(form.name.trim(), form.description.trim(), form.emoji);
    setForm({ name: '', description: '', emoji: '📚' });
    setShowCreate(false);
  };

  const handleDeleteList = async (e, listId) => {
    e.stopPropagation();
    if (window.confirm('Supprimer cette liste ?')) {
      await deleteList(listId);
      if (expandedList === listId) setExpandedList(null);
    }
  };

  return (
    <div className="page reading-lists-page">
      <div className="rl-header">
        <div>
          <h1 className="rl-title">Mes Listes</h1>
          <p className="rl-count">{myLists.length} liste{myLists.length > 1 ? 's' : ''}</p>
        </div>
        <motion.button
          className="rl-create-btn"
          onClick={() => setShowCreate(!showCreate)}
          whileTap={{ scale: 0.95 }}
        >
          <IoAdd /> Créer
        </motion.button>
      </div>

      {/* Formulaire création */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            className="rl-create-card"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <form onSubmit={handleCreate} className="rl-create-form">
              <div className="rl-emoji-picker">
                {EMOJIS.map(emoji => (
                  <button
                    type="button"
                    key={emoji}
                    className={'rl-emoji-option ' + (form.emoji === emoji ? 'rl-emoji-active' : '')}
                    onClick={() => setForm({ ...form, emoji })}
                  >
                    {emoji}
                  </button>
                ))}
              </div>

              <div className="input-group">
                <label>Nom de la liste</label>
                <input
                  className="input"
                  placeholder="Ex: Mes coups de cœur"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  maxLength={50}
                />
              </div>

              <div className="input-group">
                <label>Description (optionnel)</label>
                <input
                  className="input"
                  placeholder="De quoi parle cette liste ?"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  maxLength={120}
                />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>Créer la liste</button>
                <button type="button" className="btn btn-ghost" onClick={() => setShowCreate(false)}><IoClose /></button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Listes */}
      <div className="rl-lists">
        {myLists.length > 0 ? (
          myLists.map((list, i) => (
            <motion.div
              key={list.id}
              className="rl-list-card"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <div
                className="rl-list-header"
                onClick={() => setExpandedList(expandedList === list.id ? null : list.id)}
              >
                <span className="rl-list-emoji">{list.emoji}</span>
                <div className="rl-list-info">
                  <h3 className="rl-list-name">{list.name}</h3>
                  <p className="rl-list-meta">
                    {list.books.length} livre{list.books.length > 1 ? 's' : ''}
                    {list.description && ' · ' + list.description}
                  </p>
                </div>
                <motion.div
                  animate={{ rotate: expandedList === list.id ? 90 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <IoChevronForward style={{ fontSize: 18, opacity: 0.3 }} />
                </motion.div>
              </div>

              {/* Mini preview covers */}
              {expandedList !== list.id && list.books.length > 0 && (
                <div className="rl-list-preview">
                  {list.books.slice(0, 5).map((book, j) => (
                    <div
                      key={book.id}
                      className="rl-preview-cover"
                      style={{ background: book.coverGradient, zIndex: 5 - j, marginLeft: j > 0 ? -12 : 0 }}
                    />
                  ))}
                </div>
              )}

              {/* Liste étendue */}
              <AnimatePresence>
                {expandedList === list.id && (
                  <motion.div
                    className="rl-list-expanded"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    {list.books.length > 0 ? (
                      list.books.map(book => (
                        <div key={book.id} className="rl-book-item">
                          <div
                            className="rl-book-cover"
                            style={{ background: book.coverGradient }}
                            onClick={() => navigate('/read/' + book.id)}
                          >
                            <IoBook style={{ color: 'white', fontSize: 16, opacity: 0.6 }} />
                          </div>
                          <div className="rl-book-info" onClick={() => navigate('/read/' + book.id)}>
                            <h4 className="rl-book-title">{book.title}</h4>
                            <p className="rl-book-author">{book.author}</p>
                          </div>
                          <motion.button
                            className="rl-book-remove"
                            onClick={() => removeBookFromList(list.id, book.id)}
                            whileTap={{ scale: 0.8 }}
                          >
                            <IoClose />
                          </motion.button>
                        </div>
                      ))
                    ) : (
                      <p className="rl-list-empty">
                        Cette liste est vide. Ajoute des livres depuis le lecteur !
                      </p>
                    )}

                    <button
                      className="rl-delete-btn"
                      onClick={(e) => handleDeleteList(e, list.id)}
                    >
                      <IoTrash /> Supprimer cette liste
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <p className="empty-state-text">
              Crée ta première liste de lecture ! Organise tes livres par thème, humeur ou envie.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}